import { useLoaderData, useSearchParams, redirect } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ReportTable } from "@/components/reports/ReportTable";
import { fetchUserProfile } from "@/hooks/useUser";
import { queryClient } from "@/lib/queryClient";
import {
  EMPTY_REPORTS_DISTRIBUTIONS,
  fetchReportsDistributions,
  getReportsDistributionsQueryKey,
  getReportsTeamScope,
  REPORTS_STALE_TIME,
  useReportsData,
  type ReportsLoaderData,
} from "@/hooks/useReportsData";

const REPORT_TABS = [
  {
    id: "site-types",
    label: "By Site Types",
    title: "Site Types Distribution",
  },
  { id: "geo", label: "By GEO", title: "GEO Distribution" },
  {
    id: "keywords",
    label: "By Keywords",
    title: "Brand Keywords Distribution",
  },
  { id: "languages", label: "By Languages", title: "Languages Distribution" },
] as const;

export async function reportsLoader() {
  try {
    const { profile } = await queryClient.fetchQuery({
      queryKey: ["authUser"],
      queryFn: fetchUserProfile,
      staleTime: REPORTS_STALE_TIME,
    });

    const teamScope = getReportsTeamScope(profile);

    await queryClient.prefetchQuery({
      queryKey: getReportsDistributionsQueryKey(teamScope),
      queryFn: fetchReportsDistributions,
      staleTime: REPORTS_STALE_TIME,
    });

    return { teamScope };
  } catch {
    return redirect("/login");
  }
}

export default function Reports() {
  const { teamScope } = useLoaderData() as ReportsLoaderData;
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, isFetching } = useReportsData(teamScope);

  const distributions = data ?? EMPTY_REPORTS_DISTRIBUTIONS;
  const currentTab = searchParams.get("tab") || "site-types";

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  return (
    <div className="space-y-8 h-full">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">
          Detailed Reports
        </h1>
        <p className="mt-2 text-muted-foreground">
          Full distribution breakdown of your domain portfolio.
        </p>
      </div>

      <Tabs
        value={currentTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-4 mb-8">
          {REPORT_TABS.map((tab) => (
            <TabsTrigger className="cursor-pointer" key={tab.id} value={tab.id}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <div
          className={cn(
            "transition-opacity",
            isFetching && "opacity-50 pointer-events-none",
          )}
        >
          {REPORT_TABS.map((tab) => (
            <TabsContent key={tab.id} value={tab.id}>
              <ReportTable
                title={tab.title}
                data={distributions[tab.id] || []}
              />
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </div>
  );
}
